import { Dialog } from '@angular/cdk/dialog';
import { inject } from '@angular/core';
import { AssessmentApplicationMode, PostItem } from '@app/core/models';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { ModalAssessmentsMirrorComponent } from '@app/modules/assessments-manager/shared/components/modal-assessments-mirror/modal-assessments-mirror.component';
import { ModalAssessmentExecutionService } from '@app/modules/assessments-manager/shared/modal-assessment-execution/modal-assessment-execution.service';
import { Checker, notNull } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { of, switchMap, take } from 'rxjs';
import { Header } from '../../utils/postable';
import { ItemFn } from '../item-post/item-post.component';
import { ExtraExamComponent } from './extra-exam.component';
import { SubtitleExamComponent } from './subtitle-exam.component';
import { FilterControlEnum, FilterForm, StatusFilter, StatusFilterTypeEnum } from '../posts-filter-bar/filter-bar';

interface Service {
  store: Store;
  dialog: Dialog;
  modalAssessmentExecutionService: ModalAssessmentExecutionService;
}

export const CPBProvaFilter = (state: AppState) => ({
  status: status(state),
  filters: filters(state),
});

export const CPBProvaHeader: Header = {
  plural: 'eclass.agenda.filtros.cpb-prova.plural',
  singular: 'eclass.agenda.filtros.cpb-prova.singular',
  icon: 'icon-gestor-avaliacoes',
  worker: EclassWorkerEnum.CpbProvas,
};

export const CPBProvaItem: ItemFn = () => {
  const services: Service = {
    store: inject(Store),
    dialog: inject(Dialog),
    modalAssessmentExecutionService: inject(ModalAssessmentExecutionService),
  };

  return {
    ...CPBProvaHeader,
    color: 'border-modules-assessments-manager-500 outline-modules-assessments-manager-500',
    iconStyle: 'text-modules-assessments-manager-700 bg-modules-assessments-manager-500/10',
    labelStyle: 'border-modules-assessments-manager-500 text-modules-assessments-manager-700',
    subtitle: SubtitleExamComponent,
    extra: ExtraExamComponent,
    action: (item) => readOnly(item, services),
    options: () => null,
  };
};

function readOnly(item: PostItem, services: Service) {
  const activeUser$ = services.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) => {
      if (item.metadata.mode === AssessmentApplicationMode.Impressa) {
        services.dialog.open(ModalAssessmentsMirrorComponent, {
          data: {
            userId: activeUser.usuario_id,
            userName: activeUser.nome,
            entityId: activeUser.entidade_id,
            evaluationUuid: item.metadata.application_uuid,
            evaluationTitle: item.title,
          },
        });
      } else {
        services.modalAssessmentExecutionService.open({
          data: { aplicacao_uuid: item.metadata.application_uuid },
        });
      }

      return of();
    }),
  );
}

function status(store: AppState): StatusFilter[] {
  const status: StatusFilter[] = [
    {
      key: StatusFilterTypeEnum.IsConcluded,
      type: StatusFilterTypeEnum.IsConcluded,
      value: undefined,
      description: 'geral.todos',
    },
  ];
  if (Checker.isStudentOrResponsible(store.funcao_padrao)) {
    status.push(
      {
        key: `${StatusFilterTypeEnum.IsConcluded}#1`,
        type: StatusFilterTypeEnum.IsConcluded,
        value: 1,
        description: 'eclass.relatorios.concluidos',
      },
      {
        key: `${StatusFilterTypeEnum.IsConcluded}#0`,
        type: StatusFilterTypeEnum.IsConcluded,
        value: 0,
        description: 'eclass.relatorios.pendentes',
      },
    );
  }
  return status;
}

function filters(store: AppState): FilterForm {
  const filters = [FilterControlEnum.Group, FilterControlEnum.Subject, FilterControlEnum.Generic];
  if (Checker.isEmployee(store.funcao_padrao) && !Checker.isTeacher(store.funcao_padrao)) {
    filters.push(FilterControlEnum.Level, FilterControlEnum.Author);
  }
  return {
    fixed: [FilterControlEnum.Group, FilterControlEnum.Subject],
    filters,
  };
}
